import { cn } from './cn';
import Button from './Input2/Button';
import ButtonA from './Input2/ButtonA';
import { Tooltip } from './Tooltip';
import { WaveSpinner } from './WaveSpinner';
import { Alerts, Arrows, General } from './StrokeIcons';

export type IdentifierRowProps = {
  // Mapping data (flattened)
  foreignSource: 'linkedin' | 'salesnav' | string;
  foreignId: string | null;
  createdAt: Date;
  lastFailedAt: Date | null;

  // Callbacks
  onDelete: () => void;
  onVerify: () => void;
  onCopy: () => void;

  // Loading states
  isDeleting: boolean;
  isVerifying: boolean;
};

export function IdentifierRow({
  foreignSource,
  foreignId,
  createdAt,
  lastFailedAt,
  onDelete,
  onVerify,
  onCopy,
  isDeleting,
  isVerifying,
}: IdentifierRowProps) {
  const sourceLabel = foreignSource === 'salesnav' ? 'SN' : 'LI';
  const sourceColor =
    foreignSource === 'salesnav'
      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
      : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';

  // Display the full identifier — let CSS `truncate` ellipsize based on actual width.
  // The tooltip already shows the full value on hover, so no need to pre-truncate in JS
  // (which was unconditionally chopping any id >20 chars even when there was room to fit).
  const displayId = foreignId ?? '(empty)';

  // Build LinkedIn URL - use /in/ format for both sources
  const linkedInUrl = `https://www.linkedin.com/in/${foreignId}`;

  // Check if this mapping is currently failing (lastFailedAt is more recent than createdAt)
  const isFailing = lastFailedAt && createdAt && new Date(lastFailedAt) > new Date(createdAt);

  const failedDateStr = lastFailedAt ? new Date(lastFailedAt).toLocaleDateString() : '';

  const verifiedDateStr = createdAt ? new Date(createdAt).toLocaleDateString() : '';

  return (
    <div className="group flex items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800/50">
      {/* Source badge - centered between both lines */}
      <span className={cn('shrink-0 rounded-sm px-1.5 py-0.5 text-[10px] font-medium', sourceColor)}>{sourceLabel}</span>

      {/* Right side: two lines */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* First line: identifier and action buttons */}
        <div className="flex items-center gap-2">
          {/* Failure indicator */}
          {isFailing && (
            <Tooltip label={`Lookup failed on ${failedDateStr}`}>
              <Alerts.AlertCircle className="size-3 shrink-0 text-red-500" />
            </Tooltip>
          )}

          {/* Identifier value */}
          <Tooltip label={foreignId ?? '(empty)'}>
            <span
              className={cn(
                'flex-1 truncate font-mono text-xs',
                isFailing ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400',
              )}>
              {displayId}
            </span>
          </Tooltip>

          {/* Verify button (visible on hover) */}
          {foreignId && (
            <Button
              size="xs"
              text
              icon={isVerifying ? <WaveSpinner className="size-3" /> : <Arrows.RefreshCw04 className="size-3" />}
              onClick={onVerify}
              disabled={isVerifying}
              className="opacity-0 transition-opacity group-hover:opacity-100"
              tooltip="Verify identifier"
            />
          )}

          {/* Copy button (visible on hover) */}
          {foreignId && (
            <Button
              size="xs"
              text
              icon={<General.Copy01 className="size-3" />}
              onClick={onCopy}
              className="opacity-0 transition-opacity group-hover:opacity-100"
              tooltip="Copy identifier"
            />
          )}

          {/* Open in LinkedIn button (visible on hover) */}
          {foreignId && (
            <ButtonA
              size="xs"
              text
              href={linkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
              icon={<General.LinkExternal01 className="size-3" />}
              className="opacity-0 transition-opacity group-hover:opacity-100"
              tooltip="Open in LinkedIn"
            />
          )}

          {/* Delete button (visible on hover) */}
          <Button
            size="xs"
            text
            color="red"
            icon={<General.Trash01 className="size-3" />}
            onClick={onDelete}
            disabled={isDeleting}
            className="opacity-0 transition-opacity group-hover:opacity-100"
            tooltip="Delete identifier"
          />
        </div>

        {/* Second line: last verified date */}
        <span className="text-[9px] leading-tight text-gray-400 dark:text-gray-500">Verified: {verifiedDateStr}</span>
      </div>
    </div>
  );
}

export default IdentifierRow;
